import React from 'react';
import { Task } from '../types/todo';
import { Plane, Building, ListChecks, PartyPopper, Clock, MessageSquare, Users, CheckCircle2, X, ChevronUp, ChevronDown, Shield, Zap, Home, Luggage } from 'lucide-react';
import { TaskItem } from './TaskItem';
import { collection, query, where, getDocs, getCountFromServer } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface TaskCategoryProps {
  category: string;
  tasks: Task[];
  allTasks?: Task[];  // Toutes les tâches, y compris celles cachées
  isPreview: boolean;
  departureDate?: string;
  returnDate?: string;
  onUpdateStatus: (taskId: string, status: string) => void;
  onDeleteTask: (taskId: string) => void;
  onAddSubtask: (taskId: string) => void;
  onToggleSubtask: (taskId: string, subtaskId: string) => void;
  onUpdateNotes: (taskId: string, notes: string) => void;
  onEditTask: (task: Task) => void;
  onDeleteSubtask: (taskId: string, subtaskId: string) => void;
  onUpdatePriority: (taskId: string, priority: string) => void;
}

export function TaskCategory({
  category,
  tasks,
  allTasks,
  isPreview,
  departureDate,
  returnDate,
  onUpdateStatus,
  onDeleteTask,
  onAddSubtask,
  onToggleSubtask,
  onUpdateNotes,
  onEditTask,
  onDeleteSubtask,
  onUpdatePriority
}: TaskCategoryProps) {
  const [isCollapsed, setIsCollapsed] = React.useState(true);
  const [messageStats, setMessageStats] = React.useState<{
    totalMessages: number;
    activeUsers: number;
    lastMessageTime: Date | null;
  }>({
    totalMessages: 0,
    activeUsers: 0,
    lastMessageTime: null
  });

  React.useEffect(() => {
    const fetchMessageStats = async () => {
      if (!isPreview) {
        try {
          const messagesRef = collection(db, 'messages');
          const q = query(
            messagesRef,
            where('thread_code', '==', category),
            where('status', '==', 'active')
          );
          
          const totalMessagesSnapshot = await getCountFromServer(q);

          setMessageStats({
            totalMessages: totalMessagesSnapshot.data().count,
            activeUsers: 0,
            lastMessageTime: null
          });
        } catch (error) {
          console.error('Error fetching message stats:', error);
        }
      }
    };

    if (!isPreview) {
      fetchMessageStats();
    }
  }, [category, isPreview]);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'before-departure':
        return <ListChecks className="w-6 h-6 text-blue-500" />;
      case 'after-arrival':
        return <Plane className="w-6 h-6 text-green-500" />;
      case 'return':
        return <Building className="w-6 h-6 text-purple-500" />;
      case 'packing':
        return <Luggage className="w-6 h-6 text-amber-500" />;
      default:
        return null;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'before-departure':
        return 'Préparer votre départ';
      case 'after-arrival':
        return 'Le jour du départ';
      case 'return':
        return 'Les démarches à l\'arrivée';
      case 'packing':
        return 'Préparer sa valise';
      default:
        return category;
    }
  };

  const getCategoryStats = (tasks: Task[], allTasks?: Task[]) => {
    // Utiliser toutes les tâches (y compris cachées) s'il y en a
    const tasksToCount = allTasks || tasks;
    
    // Filtrer uniquement les tâches de cette catégorie
    const categoryTasks = tasksToCount.filter(t => t.category === category);
    
    const total = categoryTasks.length;
    const completed = categoryTasks.filter(t => t.status === 'completed').length;
    const inProgress = categoryTasks.filter(t => t.status === 'in-progress').length;
    const notStarted = total - completed - inProgress;
    const completionPercentage = Math.round((completed / total) * 100);

    const subtasks = categoryTasks.reduce((acc, task) => acc + task.subtasks.length, 0);
    const completedSubtasks = categoryTasks.reduce(
      (acc, task) => acc + task.subtasks.filter(st => st.completed).length,
      0
    );

    return {
      total,
      completed,
      inProgress,
      notStarted,
      completionPercentage,
      subtasks,
      completedSubtasks,
      allCompleted: completed === total && total > 0
    };
  };

  const getDaysUntilDeparture = () => {
    if (!departureDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const departure = new Date(departureDate);
    const diffTime = departure.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const stats = getCategoryStats(tasks, allTasks);
  const daysUntilDeparture = getDaysUntilDeparture();

  const getTypeColor = (category: string) => {
    switch (category) {
      case 'before-departure':
        return 'from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-100/50 dark:border-blue-800/30';
      case 'after-arrival':
        return 'from-green-50 to-green-100/50 dark:from-green-900/20 dark:to-green-800/20 border-green-100/50 dark:border-green-800/30';
      case 'return':
        return 'from-purple-50 to-purple-100/50 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-100/50 dark:border-purple-800/30';
      case 'packing':
        return 'from-amber-50 to-amber-100/50 dark:from-amber-900/20 dark:to-amber-800/20 border-amber-100/50 dark:border-amber-800/30';
      default:
        return 'from-gray-50 to-white dark:from-gray-800/50 dark:to-gray-800 border-gray-100/50 dark:border-gray-800/30';
    }
  };

  const getTypeDescription = (category: string) => {
    switch (category) {
      case 'before-departure':
        return 'Toutes les démarches à effectuer avant de partir';
      case 'after-arrival':
        return 'Ce qu\'il faut faire à votre arrivée à destination';
      case 'return':
        return 'Les démarches pour bien préparer votre retour';
      case 'packing':
        return 'Tout ce qu\'il faut mettre dans votre valise';
      default:
        return '';
    }
  };

  const getBorderColor = (category: string) => {
    switch (category) {
      case 'before-departure':
        return 'from-blue-400 to-blue-500';
      case 'after-arrival':
        return 'from-green-400 to-green-500';
      case 'return':
        return 'from-purple-400 to-purple-500';
      case 'packing':
        return 'from-amber-400 to-amber-500';
      default:
        return 'from-gray-400 to-gray-500';
    }
  };

  const renderTimeMessage = () => {
    if (category !== 'before-departure' || !departureDate || isPreview) return null;

    if (stats.allCompleted) {
      return (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/30 rounded-lg p-4 mb-4">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <PartyPopper className="w-6 h-6 text-emerald-500 dark:text-emerald-400 animate-bounce" />
            </div>
            <div>
              <h4 className="text-base font-medium text-emerald-800 dark:text-emerald-300">
                Félicitations ! 🎉
              </h4>
              <p className="text-sm text-emerald-600 dark:text-emerald-400">
                Vous avez terminé toutes les tâches de cette étape. Vous êtes prêt{daysUntilDeparture && daysUntilDeparture > 0 ? ` et il vous reste ${daysUntilDeparture} jours avant le départ` : ''} !
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (daysUntilDeparture && daysUntilDeparture > 0) {
      const urgencyClass = daysUntilDeparture <= 7 
        ? 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800/30 text-red-800 dark:text-red-300'
        : daysUntilDeparture <= 30
        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800/30 text-amber-800 dark:text-amber-300'
        : 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800/30 text-blue-800 dark:text-blue-300';

      return (
        <div className={`border rounded-lg p-4 mb-4 ${urgencyClass}`}>
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-base font-medium">
                {daysUntilDeparture} jour{daysUntilDeparture > 1 ? 's' : ''} avant le départ
              </h4>
              <p className="text-sm opacity-90">
                Il vous reste {stats.total - stats.completed} tâche{stats.total - stats.completed > 1 ? 's' : ''} à finaliser dans cette étape.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div id={`category-${category}`} className="space-y-4 scroll-mt-[136px]">
      {/* Category Header */}
      <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
        {/* Decorative category indicator - Now a top border instead of left border for mobile */}
        <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${getBorderColor(category)}`} />
        
        {/* Header with gradient background */}
        <div 
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="relative overflow-hidden cursor-pointer"
        >
          <div className={`relative bg-gradient-to-br ${getTypeColor(category)} p-4 sm:p-5 pt-5`}>
            <div className="flex items-start justify-between">
              <div className="flex gap-3">
                {/* Icon */}
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white dark:bg-gray-800 shadow-sm flex items-center justify-center">
                  {getCategoryIcon(category)}
                </div>
                
                {/* Text */}
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                    {getCategoryLabel(category)}
                  </h2>
                  <p className="hidden sm:block text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-1">
                    {getTypeDescription(category)}
                  </p>
                </div>
              </div>
              
              {/* Collapse button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCollapsed(!isCollapsed);
                }}
                className="p-1.5 bg-white/80 dark:bg-gray-800/80 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 shadow-sm transition-colors"
                aria-label={isCollapsed ? 'Expand' : 'Collapse'}
              >
                {isCollapsed ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronUp className="w-4 h-4" />
                )}
              </button>
            </div>
            
            {/* Progression info */}
            <div className="mt-3 sm:mt-4">
              <div className="flex items-center justify-between mb-1.5 text-xs">
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <span>{stats.completed}/{stats.total} terminées</span>
                </div>
                <span className="font-medium text-gray-900 dark:text-white">
                  {stats.completionPercentage}%
                </span>
              </div>
              <div className="h-1.5 bg-gray-200 dark:bg-gray-700/50 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary-500 to-accent-500 transition-all duration-500"
                  style={{ width: `${stats.completionPercentage}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile stats grid - Amélioré avec des tailles fixes */}
        {!isCollapsed && (
          <div className="grid grid-cols-3 gap-2 p-3 bg-gray-50 dark:bg-gray-800/50">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-2 text-center flex flex-col items-center justify-center h-20">
              <div className="flex items-center justify-center mb-1">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300 ml-1">Terminées</span>
              </div>
              <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{stats.completed}</span>
            </div>
            
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2 text-center flex flex-col items-center justify-center h-20">
              <div className="flex items-center justify-center mb-1">
                <Clock className="w-4 h-4 text-amber-500" />
                <span className="text-xs font-medium text-amber-700 dark:text-amber-300 ml-1">En cours</span>
              </div>
              <span className="text-lg font-bold text-amber-600 dark:text-amber-400">{stats.inProgress}</span>
            </div>
            
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-2 text-center flex flex-col items-center justify-center h-20">
              <div className="flex items-center justify-center mb-1">
                <X className="w-4 h-4 text-red-500" />
                <span className="text-xs font-medium text-red-700 dark:text-red-300 ml-1">À faire</span>
              </div>
              <span className="text-lg font-bold text-red-600 dark:text-red-400">{stats.notStarted}</span>
            </div>
          </div>
        )}

        {/* Message Center Stats - Only on desktop */}
        {!isPreview && !isCollapsed && messageStats.totalMessages > 0 && (
          <div className="hidden sm:block p-4 border-t border-gray-200/50 dark:border-gray-700/50">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-white/80 dark:bg-gray-800/80 rounded-lg">
                <MessageSquare className="w-5 h-5 text-accent-500" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  Centre de messages
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Rejoignez la discussion
                </p>
              </div>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('openMessageCenter'))}
                className="ml-auto flex items-center gap-2 px-3 py-1.5 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-800 text-sm font-medium text-accent-600 dark:text-accent-400 rounded-lg transition-colors"
              >
                <span>Participer</span>
                <MessageSquare className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-3 text-center">
                <MessageSquare className="w-4 h-4 text-accent-500 mx-auto mb-1" />
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  {messageStats.totalMessages}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Messages
                </div>
              </div>

              <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-3 text-center">
                <Users className="w-4 h-4 text-accent-500 mx-auto mb-1" />
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  {messageStats.activeUsers}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Participants
                </div>
              </div>

              <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-3 text-center">
                <Clock className="w-4 h-4 text-accent-500 mx-auto mb-1" />
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Dernier message
                </div>
                <div className="text-xs font-medium text-gray-900 dark:text-white">
                  {messageStats.lastMessageTime 
                    ? new Date(messageStats.lastMessageTime).toLocaleDateString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : 'Aucun'}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Time-related messages */}
        {!isPreview && !isCollapsed && (
          <div className="p-4">
            {renderTimeMessage()}
          </div>
        )}
        
        {/* Task list - shown when expanded */}
        {!isCollapsed && (
          <div className="px-4 pb-4">
            <div className="space-y-3">
              {tasks.map(task => (
                <TaskItem
                  key={task.id}
                  task={task}
                  isPreview={isPreview}
                  departureDate={departureDate}
                  returnDate={returnDate}
                  onUpdateStatus={onUpdateStatus}
                  onEditTask={onEditTask}
                  onDeleteTask={onDeleteTask}
                  onAddSubtask={onAddSubtask}
                  onToggleSubtask={onToggleSubtask}
                  onUpdateNotes={onUpdateNotes}
                  onDeleteSubtask={onDeleteSubtask}
                  onUpdatePriority={onUpdatePriority}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}